import React, { useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useDrawolaT } from "../../storage/i18n";
import { useCloudStorage } from "../useCloudStorage";

import { CloudAccountPanel } from "./CloudAccountPanel";
import { CloudFilePickerModal } from "./CloudFilePickerModal";
import { CloudSaveDialog } from "./CloudSaveDialog";
import { ConflictModal } from "./ConflictModal";

import type { useMultiFileStore } from "../../storage/useMultiFileStore";
import type { CloudSyncApi } from "../sync";

type Props = {
  excalidrawAPI: ExcalidrawImperativeAPI;
  multiFile: ReturnType<typeof useMultiFileStore>;
  cloudSync: CloudSyncApi;
};

export const CloudSubmenu: React.FC<Props> = ({
  excalidrawAPI,
  multiFile,
  cloudSync,
}) => {
  const t = useDrawolaT();
  const cloud = useCloudStorage({ excalidrawAPI, multiFile, cloudSync });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);

  const isSignedIn = cloud.authState.status === "signed-in";
  const defaultName = excalidrawAPI.getName() || "Untitled";

  return (
    <div className="drawOla-cloud-submenu-body">
      <CloudAccountPanel
        providerId={cloud.providerId}
        setProviderId={cloud.setProviderId}
        displayName={cloud.displayName}
        authState={cloud.authState}
        onSignIn={cloud.signIn}
        onSignOut={cloud.signOut}
      />

      {isSignedIn && (
        <div className="drawOla-cloud-actions">
          <button
            type="button"
            className="drawOla-cloud-actions__item"
            onClick={() => setPickerOpen(true)}
          >
            {t("cloud.openFromCloud", { provider: cloud.displayName })}
          </button>
          <button
            type="button"
            className="drawOla-cloud-actions__item"
            onClick={() => cloud.save(defaultName)}
          >
            {t("cloud.saveToCloud", { provider: cloud.displayName })}
          </button>
          <button
            type="button"
            className="drawOla-cloud-actions__item"
            onClick={() => setSaveOpen(true)}
          >
            {t("cloud.saveAsToCloud", { provider: cloud.displayName })}
          </button>
        </div>
      )}

      <CloudFilePickerModal
        open={pickerOpen}
        listFiles={cloud.listFiles}
        onOpen={cloud.openFile}
        onRename={cloud.renameFile}
        onDelete={cloud.deleteFile}
        onClose={() => setPickerOpen(false)}
      />

      <CloudSaveDialog
        open={saveOpen}
        defaultName={defaultName}
        onSave={async (name) => {
          await cloud.saveAs(name);
        }}
        onClose={() => setSaveOpen(false)}
      />

      <ConflictModal
        open={cloud.conflictOpen}
        remoteMeta={cloud.conflictRemote}
        onOverwrite={cloud.onOverwrite}
        onLoadRemote={cloud.onLoadRemote}
        onCancel={cloud.onCancel}
      />
    </div>
  );
};
